/**
 * Silence detection for spoken rapid-fire answers (spec 02).
 *
 * Deliberately LOCAL and pure: at 15-20 questions a minute the budget is ~3s per question, so a
 * network round-trip just to decide "have they stopped talking" would spend all of it.
 * Frame-driven so it can be unit-tested without a microphone or fake timers.
 */

export interface SilenceState {
  /** Milliseconds of uninterrupted silence accumulated so far. */
  silentMs: number;
  /** True on the single frame the advance should fire. Never true twice for one silence. */
  shouldAdvance: boolean;
  /**
   * Whether the respondent has been heard speaking at all yet on this turn.
   *
   * Silence only means "they have finished" once they have started. Before that it is just the
   * room, and a respondent still reading the question would be advanced past it — which is what
   * happened in live testing. So the counter does not run until the first frame above threshold.
   */
  hasSpoken: boolean;
}

export interface SilenceOptions {
  /** Audio level at or above which we consider the respondent to be speaking. */
  threshold: number;
  /** Silence needed before advancing. */
  advanceAfterMs: number;
}

export function initialSilenceState(): SilenceState {
  return { silentMs: 0, shouldAdvance: false, hasSpoken: false };
}

export function nextSilenceState(
  prev: SilenceState,
  level: number,
  deltaMs: number,
  opts: SilenceOptions,
): SilenceState {
  if (level >= opts.threshold) {
    return { silentMs: 0, shouldAdvance: false, hasSpoken: true };
  }
  // Quiet before the first word is the room, not an answer: hold at zero so no amount of
  // waiting can advance the turn.
  if (!prev.hasSpoken) {
    return { silentMs: 0, shouldAdvance: false, hasSpoken: false };
  }
  const silentMs = prev.silentMs + deltaMs;
  // Fire exactly once: only on the frame that crosses the line.
  const shouldAdvance = prev.silentMs < opts.advanceAfterMs && silentMs >= opts.advanceAfterMs;
  return { silentMs, shouldAdvance, hasSpoken: true };
}

/**
 * Mean normalised amplitude of an analyser frame, in [0, 1].
 *
 * Computed here rather than reusing `computeAudioLevels` (`utils/audioCapture.ts`), whose output
 * is a 7-bar VISUALISER array with a 0.5 floor and saturation at ~20% of raw input — a quiet
 * room's hiss already reads above the intended silence threshold there, so auto-advance would
 * almost never fire. `computeAudioLevels` stays exactly as it is for the bar-chart display;
 * `frameLevel` is the separate, unscaled signal the VAD actually consumes.
 */
export function frameLevel(dataArray: Uint8Array): number {
  if (dataArray.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < dataArray.length; i++) sum += dataArray[i];
  return sum / dataArray.length / 255;
}
